/**
 * Module-scope realtime socket manager — ONE multiplexed socket per client.
 *
 * Extracted from RealtimeProvider so non-React singletons (the chat adapter,
 * which mounts above the React realtime context) share the same connection
 * instead of opening a second socket (PLAN §Realtime: subscriptions ride one
 * socket per client, never N per tab). RealtimeProvider stays the React face:
 * it forwards `subscribe`, mirrors `mode`, and owns query invalidation.
 */

export type RealtimeMode = "connecting" | "live" | "polling";

export type RealtimeEvent = {
  v: number;
  type: string;
  topic: string;
  seq: number;
  ts: number;
  payload: Record<string, unknown>;
};

export type TopicSignal = RealtimeEvent | { resync: true; topic: string };
export type TopicHandler = (signal: TopicSignal) => void;

const PING_INTERVAL_MS = 25_000;
const INITIAL_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 15_000;

class RealtimeSocketManager {
  private handlers = new Map<string, Set<TopicHandler>>();
  private signalListeners = new Set<(signal: TopicSignal) => void>();
  private modeListeners = new Set<(mode: RealtimeMode) => void>();
  private sequences = new Map<string, number>();
  private socket: WebSocket | null = null;
  private started = false;
  private backoff = INITIAL_BACKOFF_MS;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private currentMode: RealtimeMode = "connecting";

  get mode(): RealtimeMode {
    return this.currentMode;
  }

  start(): void {
    if (this.started) return;
    if (typeof window === "undefined" || typeof WebSocket === "undefined") {
      return;
    }
    this.started = true;
    this.connect();
  }

  /**
   * Close and reconnect immediately — used when the session identity
   * changes so private-topic auth rebinds at the new handshake.
   */
  reset(): void {
    if (!this.started) return;
    this.teardownSocket();
    this.connect();
  }

  onMode(listener: (mode: RealtimeMode) => void): () => void {
    this.modeListeners.add(listener);
    return () => {
      this.modeListeners.delete(listener);
    };
  }

  /** Fires for every event/resync on any topic (query invalidation hook). */
  onSignal(listener: (signal: TopicSignal) => void): () => void {
    this.signalListeners.add(listener);
    return () => {
      this.signalListeners.delete(listener);
    };
  }

  subscribe(topic: string, handler: TopicHandler): () => void {
    let topicHandlers = this.handlers.get(topic);
    const isNewTopic = !topicHandlers;
    if (!topicHandlers) {
      topicHandlers = new Set();
      this.handlers.set(topic, topicHandlers);
    }
    topicHandlers.add(handler);
    this.start();
    if (isNewTopic && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ op: "subscribe", topics: [topic] }));
    }
    return () => {
      const current = this.handlers.get(topic);
      if (!current) return;
      current.delete(handler);
      if (!current.size) {
        this.handlers.delete(topic);
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({ op: "unsubscribe", topics: [topic] }),
          );
        }
      }
    };
  }

  private setMode(next: RealtimeMode): void {
    if (this.currentMode === next) return;
    this.currentMode = next;
    for (const listener of this.modeListeners) listener(next);
  }

  private emit(topic: string, signal: TopicSignal): void {
    for (const listener of this.signalListeners) listener(signal);
    for (const handler of this.handlers.get(topic) ?? []) handler(signal);
  }

  private teardownSocket(): void {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    const current = this.socket;
    this.socket = null;
    current?.close();
  }

  private connect(): void {
    this.setMode("connecting");
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const next = new WebSocket(`${protocol}://${window.location.host}/ws/v1/`);
    this.socket = next;

    next.addEventListener("open", () => {
      if (next !== this.socket) return;
      this.backoff = INITIAL_BACKOFF_MS;
      this.setMode("live");
      const topics = [...this.handlers.keys()];
      if (topics.length) {
        next.send(JSON.stringify({ op: "subscribe", topics }));
      }
      // Events are notifications, not truth: every (re)connect asks the
      // consumers to REST-resync their topics.
      for (const topic of topics) {
        this.emit(topic, { resync: true, topic });
      }
      this.pingTimer = window.setInterval(() => {
        if (next.readyState === WebSocket.OPEN) {
          next.send(JSON.stringify({ op: "ping" }));
        }
      }, PING_INTERVAL_MS);
    });

    next.addEventListener("message", (message) => {
      if (next !== this.socket) return;
      let data: { op?: string; event?: RealtimeEvent };
      try {
        data = JSON.parse(String(message.data)) as typeof data;
      } catch {
        return;
      }
      if (data.op !== "event" || !data.event) return;
      const event = data.event;
      const previous = this.sequences.get(event.topic) ?? 0;
      this.sequences.set(event.topic, event.seq);
      if (previous && event.seq > previous + 1) {
        // seq gap => the client's resync signal (envelope contract).
        this.emit(event.topic, { resync: true, topic: event.topic });
      }
      this.emit(event.topic, event);
    });

    const down = () => {
      if (next !== this.socket) return;
      if (this.pingTimer) {
        window.clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      this.socket = null;
      this.setMode("polling");
      const wait = this.backoff + Math.random() * 400;
      this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
      this.reconnectTimer = window.setTimeout(() => this.connect(), wait);
    };
    next.addEventListener("close", down);
    next.addEventListener("error", () => next.close());
  }
}

export const realtimeSocket = new RealtimeSocketManager();
